import { recordShownError } from '../observability';
import { useI18n } from '../i18n';
import { refusalOf } from '../lib/httpError';
import type { ApiWarning } from '../types';
import { fillTemplate } from '../lib/fillTemplate';

/**
 * Extracts the error message from an Axios error response, and translates a
 * `code` through `t.errors[code]` with `{placeholder}` substitution.
 *
 * Call it inside the event handler that made the request. A `useApiCall` hook
 * also lived here until #274 removed it: every one of the 37 files that import
 * this module imports `apiError` or `formatWarning`, and none used the hook.
 */
export function apiError(err: unknown, fallback = 'Error'): string {
  // The answer of the server: `lib/httpError.ts` owns the read (#661, F13).
  // Since #457 every upload goes through axios, so no code throws the old
  // `body` shape of the fetch helper.
  const refusal = refusalOf(err);
  // Backend may return a translatable error: { code, params, error }.
  // If the current i18n has a template for that code, substitute {placeholder}
  // tokens from params. Falls through to raw English on miss.
  if (refusal?.code) {
    const t = useI18n.getState().t;
    const tpl: string | undefined = t.errors?.[refusal.code];
    if (typeof tpl === 'string') return fillTemplate(tpl, refusal.params, t);
  }
  return refusal?.error || (err instanceof Error ? err.message : undefined) || fallback;
}

/**
 * The message of a refused request for an INLINE error (under a field, in a
 * row), recorded as `ui_error_shown { surface: 'inline' }` (#504). A toast
 * goes through `useToast().showError`, which records `surface: 'toast'`.
 * Before #504 an inline message recorded nothing, so a refusal that an
 * official read under a field was invisible in the telemetry.
 * `frontend-shown-errors-recorded.spec.ts` fails on a component that calls
 * `apiError()` directly.
 */
export function inlineError(err: unknown, fallback?: string): string {
  // #666 — When a sign-out ended the request, `recordShownError()` records
  // nothing. The function still returns the text. A row uses the text to show
  // that it failed (`BulkRegisterModal`, `CheckinTab`). The page of the row
  // closes with the sign-out.
  recordShownError(err, 'inline');
  return apiError(err, fallback);
}

// The type lives in types/index.ts (#664, F07), so api/client.ts types its answers with it.
// This line keeps every `import { type ApiWarning } from 'useApiCall'` working.
export type { ApiWarning };

/**
 * Renders a backend warning via the shared `t.errors[code]` i18n templates
 * with `{placeholder}` substitution from `warning.params`. Falls back to the
 * raw code if no translation exists so we never silently drop a warning.
 *
 * Backend endpoints that return non-blocking advisories (POST/PATCH /api/bouts
 * returning `{ warnings: [...] }`) use this to surface amber-warning toasts.
 */
export function formatWarning(w: ApiWarning): string {
  const t = useI18n.getState().t;
  const tpl: string | undefined = t.errors?.[w.code];
  // #603 — no ⚠ in the text: the warning toast draws its own icon.
  if (typeof tpl !== 'string') return w.code;
  return fillTemplate(tpl, w.params, t);
}

/**
 * All the warnings of one answer as ONE text, each through `formatWarning()`,
 * joined with ' · ' (code-patterns.md § Toast kinds). An empty or a missing
 * list gives ''. A second warning toast replaces the first, so every site
 * shows the warnings of an answer in one toast (#605). `useAfterWrite()` is
 * the place that shows them; a site that only joins them, with no write to
 * follow, calls this.
 */
export function formatWarnings(ws?: ApiWarning[] | null): string {
  return (ws ?? []).map(w => formatWarning(w)).join(' · ');
}
